import { questionGroupsHeaderUtil } from '@doltech/ui/lib/figma/QuestionGroupGuideline/utils/questionGroupsHeaderUtil';
import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import { get } from 'lodash';
import { types as t } from 'mobx-state-tree';

import { QuestionGroup } from '../../question-data-model/question-group.store';
import { defaultSlateValue } from '../../question-data-model/SlateObj/slate-editor.store';
import {
  DoTestCompletionNoHintQuestion,
  DoTestCompletionNoHintQuestionInstance,
} from './Question/do-test-completion-no-hint-question.store';
import {
  DoTestCompletionWithHintQuestion,
  DoTestCompletionWithHintQuestionInstance,
} from './Question/do-test-completion-with-hint-question.store';
import {
  DoTestDiagramLabelCompletionQuestion,
  DoTestDiagramLabelCompletionQuestionInstance,
} from './Question/do-test-diagram-label-completion.store';
import { DoTestDragInInstance, DoTestDragInQuestion } from './Question/do-test-drag-in.store';
import { DoTestDragOutInstance, DoTestDragOutQuestion } from './Question/do-test-drag-out.store';
import {
  DoTestFlowChartComplexQuestion,
  DoTestFlowChartComplexQuestionInstance,
} from './Question/do-test-flow-chart-complex.store';
import { DoTestFlowChartQuestion, DoTestFlowChartQuestionInstance } from './Question/do-test-flowchart-question.store';
import {
  DoTestMatchingEndingQuestion,
  DoTestMatchingEndingQuestionInstance,
} from './Question/do-test-matching-ending-question.store';
import {
  DoTestMatchingFeatureNameQuestion,
  DoTestMatchingFeatureNameQuestionInstance,
} from './Question/do-test-matching-feature-name-question.store';
import {
  DoTestMatchingHeadingQuestion,
  DoTestMatchingHeadingQuestionInstance,
} from './Question/do-test-matching-heading-question.store';
import {
  DoTestMatchingParagraphQuestion,
  DoTestMatchingParagraphQuestionInstance,
} from './Question/do-test-matching-paragraph-question.store';
import {
  DoTestMultipleAnswerQuestion,
  DoTestMultipleAnswerQuestionInstance,
} from './Question/do-test-multiple-answer-question.store';
import {
  DoTestSentenceCompleteQuestion,
  DoTestSentenceCompleteQuestionInstance,
} from './Question/do-test-sentence-complete-question.store';
import { DoTestShortQuestion, DoTestShortQuestionInstance } from './Question/do-test-short-question.store';
import {
  DoTestSingleAnswerQuestion,
  DoTestSingleAnswerQuestionInstance,
} from './Question/do-test-single-answer-question.store';
import { DoTestTableCompletion, DoTestTableCompletionInstance } from './Question/do-test-table-completion.store';
import { DoTestTFNQuestion, DoTestTFNQuestionInstance } from './Question/do-test-tfn-question.store';
import { DoTestYNNQuestion, DoTestYNNQuestionInstance } from './Question/do-test-ynn-question.store';

const getQuestionModelFromType = (type: QuestionTypeEnum) => {
  switch (type) {
    case QuestionTypeEnum.SINGLE_ANSWER:
      return DoTestSingleAnswerQuestion;
    case QuestionTypeEnum.TFN_ANSWER:
      return DoTestTFNQuestion;
    case QuestionTypeEnum.YNN_ANSWER:
      return DoTestYNNQuestion;
    case QuestionTypeEnum.MULTIPLE_ANSWER:
      return DoTestMultipleAnswerQuestion;
    case QuestionTypeEnum.MATCHING_HEADING:
      return DoTestMatchingHeadingQuestion;
    case QuestionTypeEnum.MATCHING_PARAGRAPH:
      return DoTestMatchingParagraphQuestion;
    case QuestionTypeEnum.MATCHING_ENDING:
      return DoTestMatchingEndingQuestion;
    case QuestionTypeEnum.MATCHING_FEATURE_NAME:
      return DoTestMatchingFeatureNameQuestion;
    case QuestionTypeEnum.SHORT_ANSWER:
      return DoTestShortQuestion;
    case QuestionTypeEnum.SENTENCE_COMPLETE:
      return DoTestSentenceCompleteQuestion;
    case QuestionTypeEnum.NOTE_COMPLETION_NO_HINT:
      return DoTestCompletionNoHintQuestion;
    case QuestionTypeEnum.FLOWCHART_ANSWER:
      return DoTestFlowChartQuestion;
    case QuestionTypeEnum.NOTE_COMPLETION_WITH_HINT:
      return DoTestCompletionWithHintQuestion;
    case QuestionTypeEnum.TABLE_COMPLETION:
      return DoTestTableCompletion;
    case QuestionTypeEnum.DIAGRAM_LABEL_COMPLETION:
      return DoTestDiagramLabelCompletionQuestion;
    case QuestionTypeEnum.DRAG_IN:
      return DoTestDragInQuestion;
    case QuestionTypeEnum.DRAG_OUT:
      return DoTestDragOutQuestion;
    case QuestionTypeEnum.FLOW_CHART_COMPLEX:
      return DoTestFlowChartComplexQuestion;
    default:
      throw new Error(`Cannot resolve Question Type "${type}"`);
  }
};

export const TestQuestionGroupStore = QuestionGroup.props({
  questions: t.maybeNull(
    t.array(
      t.union(
        {
          dispatcher: (s) => {
            return getQuestionModelFromType(s.type);
          },
        },
        DoTestSingleAnswerQuestion,
        DoTestTFNQuestion,
        DoTestYNNQuestion,
        DoTestMultipleAnswerQuestion,
        DoTestMatchingHeadingQuestion,
        DoTestMatchingParagraphQuestion,
        DoTestMatchingEndingQuestion,
        DoTestMatchingFeatureNameQuestion,
        DoTestShortQuestion,
        DoTestSentenceCompleteQuestion,
        DoTestCompletionNoHintQuestion,
        DoTestFlowChartQuestion,
        DoTestCompletionWithHintQuestion,
        DoTestTableCompletion,
        DoTestDiagramLabelCompletionQuestion,
        DoTestDragInQuestion,
        DoTestDragOutQuestion,
        DoTestFlowChartComplexQuestion
      )
    )
  ),
})
  .named('TestQuestionGroupStore')
  .views((self) => ({
    get groupName(): string {
      if (self.questions.length > 0) {
        return self.questions[0].groupName;
      }
      throw new Error("Group doesn't contain question");
    },
    get questionCount() {
      return self.questions.reduce((p, n) => p + get(n, 'bottomBarQuestionNoes.length', 0), 0);
    },
    get answerCount() {
      return self.questions.reduce((p, n) => p + get(n, 'answerCount', 0), 0);
    },

    get guidelineContent() {
      return self?.guideline?.content || defaultSlateValue;
    },
    getQuestionGroupsFromNoToNo() {
      return questionGroupsHeaderUtil(self.questions);
    },
  }));

export type DoTestQuestionType =
  | DoTestSingleAnswerQuestionInstance
  | DoTestTFNQuestionInstance
  | DoTestYNNQuestionInstance
  | DoTestMultipleAnswerQuestionInstance
  | DoTestMatchingHeadingQuestionInstance
  | DoTestMatchingParagraphQuestionInstance
  | DoTestMatchingEndingQuestionInstance
  | DoTestMatchingFeatureNameQuestionInstance
  | DoTestShortQuestionInstance
  | DoTestSentenceCompleteQuestionInstance
  | DoTestCompletionNoHintQuestionInstance
  | DoTestFlowChartQuestionInstance
  | DoTestCompletionWithHintQuestionInstance
  | DoTestTableCompletionInstance
  | DoTestDiagramLabelCompletionQuestionInstance
  | DoTestDragInInstance
  | DoTestDragOutInstance
  | DoTestFlowChartComplexQuestionInstance;
